import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';

import { PermissionRoute } from '@/console/app/protected-route/permission-route';
import { prefetchHomeCards } from '@/console/app/routes/root/home-card-prefetch';
import {
  MainContainer,
  RootContainer,
} from '@/console/app/routes/root/root.styled';
import { ConsoleSidebar } from '@/console/features/console-sidebar/console-sidebar';
import { switchExperience } from '@/console/host/experience-switch';
import { isModernRouteSupported } from '@/console/host/route-support';
import { AiIcon } from '@/shared/assets/icons/ai-icon';
import { MaintenanceAlert } from '@/shared/components/ui/alert/variant/maintenance-alert';
import { Chip } from '@/shared/components/ui/chip/chip';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { OverlayLoader } from '@/shared/components/ui/overlay-loader/overlay-loader';
import { Header } from '@/shared/features/header/header';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag/use-feature-flag';
import {
  FEATURE_FACTORY_OPERATOR,
  FEATURE_MODERN_UI,
} from '@/shared/hooks/use-feature-flag/use-feature-flag.constants';
import { useNavigationLoader } from '@/shared/hooks/use-navigation-loader/use-navigation-loader';
import { useUiPreferencesSync } from '@/shared/hooks/use-ui-preferences-sync/use-ui-preferences-sync';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { identApi } from '@/shared/redux/api/ident/ident-api';
import store from '@/shared/redux/store/store';

// The chat drags a markdown pipeline with a syntax highlighter behind it, half a
// megabyte of source that only tenants with the flag ever render.
const FactoryOperator = lazy(() =>
  import('@/console/features/factory-operator/factory-operator').then(
    (module) => ({ default: module.FactoryOperator }),
  ),
);

export const rootLoader = async ({ request }: { request: Request }) => {
  const user = selectUser(store.getState());

  if (!user) {
    return null;
  }

  void store.dispatch(identApi.endpoints.ReadTenant.initiate());
  void store.dispatch(identApi.endpoints.ReadUserUiPreferences.initiate());

  if (new URL(request.url).pathname === '/') {
    prefetchHomeCards();
  }

  return null;
};

export const AppRoot = () => {
  const { t } = useTranslation();
  const displayNavigationLoader = useNavigationLoader();
  const { pathname } = useLocation();
  const { isFeatureFlagEnabled } = useFeatureFlag();
  useUiPreferencesSync();

  const canSwitchHere = isModernRouteSupported(pathname);

  const onTryModernUiClick = () => {
    if (!canSwitchHere) return;

    switchExperience('modern');
  };

  const renderExperienceSwitch = () => {
    if (!isFeatureFlagEnabled(FEATURE_MODERN_UI)) {
      return undefined;
    }

    return (
      <MenuItem
        icon={<AiIcon />}
        additionalInfo={
          <Chip status="purple" variant="outline" label={t('common:beta')} />
        }
        disabled={!canSwitchHere}
        tooltip={canSwitchHere ? undefined : t('pages:try_new_ui_unavailable')}
        onClick={onTryModernUiClick}
      >
        {t('pages:try_new_ui')}
      </MenuItem>
    );
  };

  return (
    <RootContainer>
      <ConsoleSidebar />
      <MainContainer>
        <OverlayLoader display={displayNavigationLoader} />
        <Header additionalItems={renderExperienceSwitch()} />
        <MaintenanceAlert />
        {isFeatureFlagEnabled(FEATURE_FACTORY_OPERATOR) && (
          <Suspense fallback={null}>
            <FactoryOperator />
          </Suspense>
        )}
        <PermissionRoute>
          <Outlet />
        </PermissionRoute>
      </MainContainer>
    </RootContainer>
  );
};
